import React, { useEffect } from 'react';
import { Box, BoxProps } from '@mui/material';
import { keyframes } from '@emotion/react';

// Spinning animation for loader
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

interface CWYLoaderProps {
  /**
   * Size of the loader in pixels (default: 80)
   */
  size?: number;
  /**
   * If true, displays as full screen overlay (default: false)
   */
  fullScreen?: boolean;
  /**
   * Custom container props
   */
  containerProps?: BoxProps;
  /**
   * Custom ring props
   */
  ringProps?: BoxProps;
  /**
   * Custom favicon props
   */
  faviconProps?: BoxProps;
}

/**
 * Reusable CWY Loader component with favicon and rotating ring
 */
export const CWYLoader: React.FC<CWYLoaderProps> = ({
  size = 80,
  fullScreen = false,
  containerProps,
  ringProps,
  faviconProps,
}) => {
  // Also preload on component mount as backup
  useEffect(() => {
    const img = new Image();
    img.src = '/cw-favicon.svg';
  }, []);

  // Calculate favicon size and ring width
  const faviconSize = size * 0.55; // 55% of ring size - larger favicon
  const ringWidth = size * 0.04; // 4% of size - thinner ring

  const loaderContent = (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: size,
        height: size,
        ...containerProps?.sx,
      }}
      {...containerProps}
    >
      {/* Rotating outer ring */}
      <Box
        sx={{
          position: 'absolute',
          width: size,
          height: size,
          border: `${ringWidth}px solid #e8eaed`,
          borderTop: `${ringWidth}px solid #606060`,
          borderRadius: '50%',
          animation: `${spin} 1s linear infinite`,
          ...ringProps?.sx,
        }}
        {...ringProps}
      />
      {/* CWY Favicon in center - using background-image for faster loading */}
      <Box
        sx={{
          width: faviconSize,
          height: faviconSize,
          position: 'relative',
          zIndex: 1,
          backgroundImage: 'url(/cw-favicon.svg)',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          ...faviconProps?.sx,
        }}
        {...faviconProps}
      />
    </Box>
  );

  if (fullScreen) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: '#ffffff',
          zIndex: 9999,
        }}
      >
        {loaderContent}
      </Box>
    );
  }

  return loaderContent;
};

export default CWYLoader;

